// src/products/equityManager/forms/DirectorForm.js
import React, { useState } from 'react';
import { DIRECTOR_TYPES, DIRECTOR_STATUS } from '../constants';

const DirectorForm = ({ onSubmit, onCancel, initialData = {} }) => {
  const [data, setData] = useState({
    first_name: initialData.first_name || '',
    middle_name: initialData.middle_name || '',
    last_name: initialData.last_name || '',
    email: initialData.email || '',
    is_director: initialData.is_director || false,
    is_alternate_director: initialData.is_alternate_director || false,
    is_company_secretary: initialData.is_company_secretary || false,
    date_appointed: initialData.date_appointed || new Date().toISOString().split('T')[0],
    date_end: initialData.date_end || '',
    status: initialData.status || DIRECTOR_STATUS[0],
  });

  // NEW: State for "Also create as Shareholder" checkbox
  const [createAsShareholder, setCreateAsShareholder] = useState(false);

  const handleTypeChange = (typeId, checked) => {
    setData({ ...data, [typeId]: checked });
  };

  const isAtLeastOneTypeSelected = () => {
    return data.is_director || data.is_alternate_director || data.is_company_secretary;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!isAtLeastOneTypeSelected()) {
      alert('Please select at least one director type.');
      return;
    }
    
    // Clean up data - remove date_end and middle_name if empty
    const submitData = { ...data };
    if (!submitData.date_end) {
      delete submitData.date_end;
    }
    if (!submitData.middle_name || submitData.middle_name.trim() === '') {
      delete submitData.middle_name;
    }

    // NEW: Add flag to indicate if shareholder should also be created
    if (createAsShareholder && !initialData.id) {
      submitData.createAsShareholder = true;
    }
    
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="text-lg font-bold mb-4">{initialData.id ? 'Edit Director' : 'Add New Director'}</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
            <input
              type="text"
              value={data.first_name}
              onChange={(e) => setData({ ...data, first_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
            <input
              type="text"
              value={data.middle_name}
              onChange={(e) => setData({ ...data, middle_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
            <input
              type="text"
              value={data.last_name}
              onChange={(e) => setData({ ...data, last_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={data.email}
            onChange={(e) => setData({ ...data, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Director Types * (Select at least one)</label>
          <div className="space-y-2">
            {DIRECTOR_TYPES.map(type => (
              <div key={type.id} className="flex items-center">
                <input
                  type="checkbox"
                  id={type.id}
                  checked={data[type.id]}
                  onChange={(e) => handleTypeChange(type.id, e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor={type.id} className="ml-3 text-sm font-medium text-gray-700">
                  {type.label}
                </label>
              </div>
            ))}
          </div>
          {!isAtLeastOneTypeSelected() && (
            <p className="text-red-500 text-xs mt-1">At least one director type must be selected</p>
          )}
        </div>

        {/* NEW: Also create as Shareholder checkbox - only show when adding new director */}
        {!initialData.id && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="createAsShareholder"
                checked={createAsShareholder}
                onChange={(e) => setCreateAsShareholder(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
              />
              <div className="ml-3">
                <label htmlFor="createAsShareholder" className="text-sm font-medium text-blue-900">
                  Also create as Shareholder
                </label>
                <p className="text-xs text-blue-700 mt-1">
                  Only use if the shareholding is held as an individual. This will automatically create a shareholder record using the director's details.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Appointed *</label>
            <input
              type="date"
              value={data.date_appointed}
              onChange={(e) => setData({ ...data, date_appointed: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date End (if applicable)</label>
            <input
              type="date"
              value={data.date_end}
              onChange={(e) => setData({ ...data, date_end: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
          <select
            value={data.status}
            onChange={(e) => setData({ ...data, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            {DIRECTOR_STATUS.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="flex justify-end space-x-2 mt-6">
        <button 
          type="button" 
          onClick={onCancel} 
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          {initialData.id ? 'Update Director' : 'Add Director'}
        </button>
      </div>
    </form>
  );
};

export default DirectorForm;